import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

export type ConfirmationStatus = 'Pending' | 'Confirmed' | 'Cancelled';
export type ReservationType = 'Online' | 'Opentable' | 'Phone' | 'In-person';

export interface ThanksgivingReservation {
  id: number;
  reservationDate: string;
  reservationTime: string;
  name: string;
  numberOfPeople: number;
  email: string | null;
  phone: string | null;
  tableNumber: string | null;
  notes: string | null;
  reservationType: ReservationType | null;
  confirmed: ConfirmationStatus;
  createdDate: string;
}

export interface CreateThanksgivingReservationRequest {
  reservationDate: string;
  reservationTime: string;
  name: string;
  numberOfPeople: number;
  email?: string | null;
  phone?: string | null;
  tableNumber?: string | null;
  notes?: string | null;
  reservationType?: ReservationType | null;
  confirmed?: ConfirmationStatus;
}

export interface UpdateThanksgivingReservationRequest {
  reservationDate?: string;
  reservationTime?: string;
  name?: string;
  numberOfPeople?: number;
  email?: string | null;
  phone?: string | null;
  tableNumber?: string | null;
  notes?: string | null;
  reservationType?: ReservationType | null;
  confirmed?: ConfirmationStatus;
}

export interface PaginatedThanksgivingReservationsResponse {
  data: ThanksgivingReservation[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ThanksgivingReservationParams {
  page?: number;
  limit?: number;
}

export const thanksgivingReservationsApi = {
  // The backend only supports page/limit - it has no server-side search or filtering,
  // so matching by name/phone/email/status is done client-side on the fetched page.
  getReservations: async (
    params: ThanksgivingReservationParams = {},
    token: string
  ): Promise<PaginatedThanksgivingReservationsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.THANKSGIVING_RESERVATIONS.BASE)}?${searchParams.toString()}`;

    return apiRequest<PaginatedThanksgivingReservationsResponse>(url, {
      method: 'GET',
      headers: getAuthHeaders(token),
    });
  },

  createReservation: async (
    data: CreateThanksgivingReservationRequest,
    token: string
  ): Promise<ThanksgivingReservation> => {
    return apiRequest<ThanksgivingReservation>(
      buildApiUrl(API_CONFIG.ENDPOINTS.THANKSGIVING_RESERVATIONS.BASE),
      {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  updateReservation: async (
    id: number,
    data: UpdateThanksgivingReservationRequest,
    token: string
  ): Promise<ThanksgivingReservation> => {
    return apiRequest<ThanksgivingReservation>(
      buildApiUrl(API_CONFIG.ENDPOINTS.THANKSGIVING_RESERVATIONS.BY_ID(id)),
      {
        method: 'PATCH',
        headers: getAuthHeaders(token),
        body: JSON.stringify(data),
      }
    );
  },

  deleteReservation: async (id: number, token: string): Promise<void> => {
    return apiRequest<void>(
      buildApiUrl(API_CONFIG.ENDPOINTS.THANKSGIVING_RESERVATIONS.BY_ID(id)),
      {
        method: 'DELETE',
        headers: getAuthHeaders(token),
      }
    );
  },
};
